import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '~/common/stores';
import { axiosInstance, setAxiosAuthorization } from '~/common/utils/api.util';
import { fetchMe, resetAuthUser } from '~/features/auth/stores/auth.slice';
import { getAccessToken, resetAuthData } from '~/features/auth/utils/auth.util';

import type { PropsWithChildren } from 'react';

/**
 * 글로벌 인증 초기화 컴포넌트
 * - App 레벨에서 한 번만 실행
 * - 쿠키에서 토큰 확인 후 axios 헤더 설정 및 사용자 정보 조회
 * - 실패 시 조용히 로그아웃 처리 (리다이렉트 없음)
 * - 자식 컴포넌트 렌더링을 블로킹하지 않음
 */
function AuthInitializer({ children }: PropsWithChildren) {
  const dispatch = useAppDispatch();
  const authUser = useAppSelector(state => state.auth.authUser);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current || authUser) return;
    checkedRef.current = true;

    const accessToken = getAccessToken();
    if (!accessToken) {
      dispatch(resetAuthUser());
      return;
    }

    setAxiosAuthorization(axiosInstance, accessToken);

    dispatch(fetchMe())
      .unwrap()
      .catch(() => {
        // 401 또는 기타 에러 시 조용히 로그아웃 (리다이렉트 없음)
        resetAuthData();
        dispatch(resetAuthUser());
      });
  }, [authUser, dispatch]);

  return children;
}

export default AuthInitializer;
